import React, { useState } from 'react';
import { supabase } from './supabaseClient';
import logoUrl from './assets/storsafe-logo.png';
import StepCIIU from './components/onboarding/StepCIIU';
import './components/onboarding/onboarding-pasos.css';

interface Props {
  userId: string;
  userName: string;
  companyId: string;
  onComplete: () => void;
  onGoToProducts: () => void;
  onGoToWarehouses: () => void;
  showSSOStep?: boolean;
  onGoToJobPositions: () => void;
}

type Step = 1 | 2 | 3 | 4 | 5;

// Flujo B (contratación directa sin trial): si quedaron guardados al
// registrarse, al terminar el wizard se salta el trial y se va directo
// al checkout de PagoPlux para ese plan/ciclo en vez del dashboard normal.
function getDirectHire(): { plan: string; cycle: string } | null {
  const plan = localStorage.getItem('ss_direct_hire_plan');
  if (!plan) return null;
  const cycle = localStorage.getItem('ss_direct_hire_cycle') ?? 'monthly';
  return { plan, cycle };
}

export default function OnboardingModal({
  userId,
  userName,
  companyId,
  onComplete,
  onGoToProducts,
  onGoToWarehouses,
  showSSOStep,
  onGoToJobPositions,
}: Props) {
  const [step, setStep]     = useState<Step>(1);
  const [saving, setSaving] = useState(false);
  const directHire = getDirectHire();

  async function markComplete() {
    setSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ onboarding_completed: true })
      .eq('id', userId);
    if (error) {
      alert('Error al completar el onboarding: ' + error.message);
      setSaving(false);
      return;
    }
    setSaving(false);

    if (directHire) {
      localStorage.removeItem('ss_direct_hire_plan');
      localStorage.removeItem('ss_direct_hire_cycle');
      window.location.href = `/pricing?plan=${directHire.plan}&cycle=${directHire.cycle}&autopay=true`;
      return;
    }
    onComplete();
  }

  async function handleGoToProducts() {
    await markComplete();
    onGoToProducts();
  }

  async function handleSkipStep3() {
    setSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ onboarding_completed: true })
      .eq('id', userId);
    setSaving(false);
    if (error) {
      alert('Error al omitir: ' + error.message);
      return;
    }
    setStep(4);
  }

  async function handleGoToWarehouses() {
    await markComplete();
    onGoToWarehouses();
  }

  async function handleGoToJobPositions() {
    await markComplete();
    onGoToJobPositions();
  }

  return (
    <div className="onb-overlay">
      <div className="onb-card">
        {/* Progress dots */}
        <div className="onb-progress">
          {(showSSOStep ? [1, 2, 3, 4, 5] as Step[] : [1, 2, 3, 4] as Step[]).map((s) => (
            <div key={s} className={`onb-dot ${step === s ? 'active' : step > s ? 'done' : ''}`} />
          ))}
        </div>

        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="onb-step">
            <div className="onb-logo-wrap">
              <img src={logoUrl} alt="StorSafe" className="onb-logo" />
            </div>
            <h2 className="onb-title">
              Bienvenido a StorSafe,{' '}
              <span className="onb-name">{userName.split(' ')[0]}</span>
            </h2>
            <p className="onb-desc">
              StorSafe te ayuda a gestionar tus bodegas de productos químicos de forma
              segura y cumpliendo con la NTE INEN 2266:2013. En los próximos pasos
              te guiaremos para configurar tu cuenta.
            </p>
            <div className="onb-features">
              <div className="onb-feature">
                <span className="onb-feature-icon">🧪</span>
                <span>Inventario de productos químicos con clasificación SGA</span>
              </div>
              <div className="onb-feature">
                <span className="onb-feature-icon">🏭</span>
                <span>Gestión de bodegas y zonas de almacenamiento</span>
              </div>
              <div className="onb-feature">
                <span className="onb-feature-icon">⚠️</span>
                <span>Matriz de compatibilidad e incompatibilidad química</span>
              </div>
              <div className="onb-feature">
                <span className="onb-feature-icon">📋</span>
                <span>Reportes de cumplimiento y planes de emergencia</span>
              </div>
            </div>
            <div className="onb-trial-note">
              {directHire
                ? <>Estás a un paso de activar tu plan. Completa estos pasos rápidos y continuamos con el pago.</>
                : <>Tu prueba gratuita de <strong>5 días</strong> está activa. No se requiere tarjeta de crédito.</>}
            </div>
            <button className="onb-btn-primary" onClick={() => setStep(2)}>
              Comenzar →
            </button>
          </div>
        )}

        {/* Step 2: CIIU + Perfil SST */}
        {step === 2 && (
          <StepCIIU
            companyId={companyId}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}

        {/* Step 3: First product */}
        {step === 3 && (
          <div className="onb-step">
            <div className="onb-step-icon" style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
            <h2 className="onb-title">Agrega tu primer producto</h2>
            <p className="onb-desc">
              Registra los productos químicos que almacenas en tu bodega. Incluye
              información como clase de peligro SGA, número UN y ficha de seguridad (SDS).
            </p>
            <div className="onb-steps-list">
              <div className="onb-step-item">
                <span className="onb-step-num">1</span>
                <span>Ve a <strong>Productos</strong> en el menú lateral</span>
              </div>
              <div className="onb-step-item">
                <span className="onb-step-num">2</span>
                <span>Haz clic en <strong>"Agregar producto"</strong></span>
              </div>
              <div className="onb-step-item">
                <span className="onb-step-num">3</span>
                <span>Completa el nombre, clasificación SGA y número UN</span>
              </div>
              <div className="onb-step-item">
                <span className="onb-step-num">4</span>
                <span>Opcionalmente adjunta la <strong>ficha SDS</strong> del producto</span>
              </div>
            </div>
            <div className="onb-btn-row">
              <button className="onb-btn-secondary" onClick={() => setStep(2)}>← Atrás</button>
              <button className="onb-btn-primary" onClick={handleGoToProducts} disabled={saving}>
                {saving ? 'Guardando…' : 'Ir a Productos →'}
              </button>
            </div>
            <button className="onb-btn-skip" onClick={handleSkipStep3} disabled={saving}>
              {saving ? 'Guardando…' : 'Omitir por ahora'}
            </button>
          </div>
        )}

        {/* Step 4: First warehouse */}
        {step === 4 && (
          <div className="onb-step">
            <div className="onb-step-icon" style={{ background: 'rgba(59,130,246,0.12)', color: '#3b82f6' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <h2 className="onb-title">Crea tu primera bodega</h2>
            <p className="onb-desc">
              Define las bodegas donde almacenas tus productos. Puedes crear zonas
              dentro de cada bodega y asignar productos a ellas para controlar la
              compatibilidad química.
            </p>
            <div className="onb-steps-list">
              <div className="onb-step-item">
                <span className="onb-step-num">1</span>
                <span>Ve a <strong>Bodegas</strong> en el menú lateral</span>
              </div>
              <div className="onb-step-item">
                <span className="onb-step-num">2</span>
                <span>Haz clic en <strong>"Nueva bodega"</strong></span>
              </div>
              <div className="onb-step-item">
                <span className="onb-step-num">3</span>
                <span>Ingresa nombre, ubicación y capacidad</span>
              </div>
              <div className="onb-step-item">
                <span className="onb-step-num">4</span>
                <span>Crea <strong>zonas</strong> y asigna productos para detectar incompatibilidades</span>
              </div>
            </div>
            <div className="onb-btn-row">
              <button className="onb-btn-secondary" onClick={() => setStep(3)}>← Atrás</button>
              <button className="onb-btn-primary" onClick={handleGoToWarehouses} disabled={saving}>
                {saving ? 'Guardando…' : 'Ir a Bodegas →'}
              </button>
            </div>
            <button className="onb-btn-skip" onClick={() => (showSSOStep ? setStep(5) : markComplete())} disabled={saving}>
              {saving ? 'Guardando…' : 'Finalizar y explorar'}
            </button>
          </div>
        )}

        {/* Step 5: First job position (SSO, solo empresas is_dev) */}
        {step === 5 && (
          <div className="onb-step">
            <div className="onb-step-icon" style={{ background: 'rgba(129,140,248,0.12)', color: '#818cf8' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                <rect x="2" y="7" width="20" height="14" rx="2"/>
                <line x1="12" y1="12" x2="12" y2="12.01"/>
                <path d="M2 12h20"/>
              </svg>
            </div>
            <h2 className="onb-title">Configura tu primer Puesto de Trabajo (SSO)</h2>
            <p className="onb-desc">
              Define los puestos de trabajo de tu empresa y sus riesgos típicos. Esto te
              permitirá recibir sugerencias automáticas al crear evaluaciones de Matriz de Riesgos, permisos
              de trabajo y capacitaciones — ahorrándote tiempo en cada nuevo registro.
            </p>
            <div className="onb-steps-list">
              <div className="onb-step-item">
                <span className="onb-step-num">1</span>
                <span>Ve a <strong>Puestos de Trabajo</strong> en el menú lateral (sección Seguridad y Salud)</span>
              </div>
              <div className="onb-step-item">
                <span className="onb-step-num">2</span>
                <span>Haz clic en <strong>"Nuevo puesto"</strong></span>
              </div>
              <div className="onb-step-item">
                <span className="onb-step-num">3</span>
                <span>Ingresa el nombre del puesto (ej. "Operario de Bodega")</span>
              </div>
              <div className="onb-step-item">
                <span className="onb-step-num">4</span>
                <span>Marca los <strong>riesgos típicos</strong> de ese puesto (espacios confinados, alturas, químico, etc.) — esto alimentará las sugerencias automáticas en toda la plataforma</span>
              </div>
            </div>
            <div className="onb-btn-row">
              <button className="onb-btn-secondary" onClick={() => setStep(4)}>← Atrás</button>
              <button className="onb-btn-primary" onClick={handleGoToJobPositions} disabled={saving}>
                {saving ? 'Guardando…' : 'Ir a Puestos de Trabajo →'}
              </button>
            </div>
            <button className="onb-btn-skip" onClick={markComplete} disabled={saving}>
              {saving ? 'Guardando…' : 'Finalizar y explorar'}
            </button>
          </div>
        )}
      </div>

      <style>{`
        .onb-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 500;
          padding: 24px;
          overflow-y: auto;
        }

        .onb-card {
          background: #1e293b;
          border: 1px solid #334155;
          border-radius: 20px;
          padding: 40px 36px 32px;
          width: 100%;
          max-width: 520px;
          max-height: calc(100vh - 48px);
          overflow-y: auto;
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5);
        }

        .onb-progress {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-bottom: 32px;
        }

        .onb-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #334155;
          transition: background 0.2s, transform 0.2s;
        }

        .onb-dot.active {
          background: #2FBF71;
          transform: scale(1.35);
        }

        .onb-dot.done {
          background: #166534;
        }

        .onb-logo-wrap {
          margin-bottom: 20px;
        }

        .onb-logo {
          width: 160px;
          height: auto;
          filter: drop-shadow(0 4px 16px rgba(47, 191, 113, 0.25));
        }

        .onb-name {
          color: #2FBF71;
        }

        .onb-features {
          display: flex;
          flex-direction: column;
          gap: 10px;
          width: 100%;
          margin-bottom: 24px;
        }

        .onb-feature {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          background: rgba(255,255,255,0.03);
          border: 1px solid #334155;
          border-radius: 8px;
          font-size: 0.84rem;
          color: #cbd5e1;
          text-align: left;
        }

        .onb-feature-icon {
          font-size: 1.2rem;
          flex-shrink: 0;
        }

        .onb-trial-note {
          padding: 10px 16px;
          background: rgba(245,158,11,0.08);
          border: 1px solid rgba(245,158,11,0.25);
          border-radius: 8px;
          font-size: 0.82rem;
          color: #fbbf24;
          margin-bottom: 24px;
          width: 100%;
        }

        .onb-steps-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          width: 100%;
          margin-bottom: 28px;
          text-align: left;
        }

        .onb-step-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          background: rgba(255,255,255,0.03);
          border: 1px solid #334155;
          border-radius: 8px;
          font-size: 0.84rem;
          color: #cbd5e1;
        }

        .onb-step-num {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: rgba(47,191,113,0.15);
          color: #2FBF71;
          font-size: 0.72rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        /* .onb-step, .onb-step-icon, .onb-title, .onb-desc y los botones .onb-btn-*: components/onboarding/onboarding-pasos.css */
      `}</style>
    </div>
  );
}
